import axios from 'axios';
import { baseUrl, endpoints } from './api';
import { store } from '../store/store';
import { logout } from '../store/slices/authslice';
import { trackSessionExpired } from '../utils/analytics';

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = [];
let isInterceptorSetup = false; // Prevent multiple interceptor registrations

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

const performLogout = () => {
  trackSessionExpired();
  store.dispatch(logout());
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

const InterceptorSetup = () => {
  // Prevent multiple interceptor registrations (React.StrictMode calls this twice)
  if (isInterceptorSetup) {
    return;
  }
  isInterceptorSetup = true;

  // Request interceptor
  axios.interceptors.request.use(
    async req => {
      // Only prepend baseUrl if URL doesn't already start with http (prevents double URL on retry)
      if (req.url && !req.url.startsWith('http')) {
        req.url = baseUrl + req.url;
      }
      const token = localStorage.getItem('accessToken');
      if (token) {
        req.headers['Authorization'] = 'Bearer ' + token;
      }
      return req;
    },
    err => {
      return Promise.reject(err);
    },
  );

  // Response interceptor
  axios.interceptors.response.use(
    res => res,
    async err => {
      const originalRequest = err.config;

      // Handle 401 - check if session was replaced on another device
      const isRefreshRequest = originalRequest.url?.includes('refresh-token');
      const errorType = err.response?.data?.errorType;

      if (errorType === 'SessionExpired' || errorType === 'SecurityAlert') {
        // SecurityAlert = reuse detected, all sessions wiped server-side
        if (errorType === 'SecurityAlert') {
          alert('You have been logged out for your safety. Please log in again.');
        }
        performLogout();
        return Promise.reject(err);
      }

      // Handle 401 - attempt token refresh
      // Skip refresh logic for the refresh-token endpoint itself to avoid infinite loop
      if (err.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          performLogout();
          return Promise.reject(err);
        }

        try {
          const response = await axios.post(endpoints.refreshToken, {
            refreshToken
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

          processQueue(null, accessToken);
          originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          performLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle 401 from refresh-token endpoint - logout immediately
      if (err.response?.status === 401 && isRefreshRequest) {
        performLogout();
        return Promise.reject(err.response || err);
      }

      // Handle 403 TERMS_REQUIRED — fire a window event; App.tsx listens and shows the modal
      if (err.response?.status === 403 && err.response?.data?.code === 'TERMS_REQUIRED') {
        window.dispatchEvent(new CustomEvent('terms:required'));
        return Promise.reject(err.response || err);
      }

      // Handle 403 - Forbidden
      if (err.response?.status === 403) {
        console.error('Access forbidden:', err.response.data);
      }

      // Handle 400/500 - Bad request / Server error
      if (err.response?.status === 400 || err.response?.status === 500) {
        console.error('Error:', err.response.data);
      }

      return Promise.reject(err.response || err);
    },
  );
};

export default InterceptorSetup;